import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ConnectionForm from './components/ConnectionForm';
import Header from './components/Header';
import ProjectDashboard from './components/ProjectDashboard';
import ApiHub from './components/ApiHub';
import Settings from './components/Settings';
import pocketbaseService from './services/pocketbase';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

const { FiClock, FiAlertTriangle } = FiIcons;

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('dashboard');
  const [connInfo, setConnInfo] = useState({ isConnected: false });
  const [sessionExpiry, setSessionExpiry] = useState(null);

  useEffect(() => {
    const init = async () => {
      try {
        const connected = await pocketbaseService.reconnectFromStorage();
        if (connected) {
          setIsConnected(true);
          setConnInfo(pocketbaseService.getConnectionInfo());
        }
      } catch (e) {
        console.error("Init error:", e);
      } finally {
        setIsLoading(false);
      }
    };

    const handleLogoutEvent = () => {
      setIsConnected(false);
      setConnInfo({ isConnected: false });
    };

    init();

    window.addEventListener('pb_logout', handleLogoutEvent);
    
    const timer = setInterval(() => {
      if (pocketbaseService.pb.authStore.isValid) {
        const timeLeft = pocketbaseService.getTimeUntilExpiry();
        setSessionExpiry(timeLeft);
        if (timeLeft <= 0) {
          pocketbaseService.disconnect();
        }
      }
    }, 10000);

    return () => {
      window.removeEventListener('pb_logout', handleLogoutEvent);
      clearInterval(timer);
    };
  }, []);

  const handleConnect = async (formData) => {
    setIsLoading(true);
    setError(null);
    try {
      await pocketbaseService.connect(formData.url, formData.email, formData.password);
      setIsConnected(true);
      setConnInfo(pocketbaseService.getConnectionInfo());
    } catch (err) {
      setError("Falha na autenticação. Verifique URL e credenciais de Superuser.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (ms) => {
    if (!ms || ms < 0) return '0s';
    const mins = Math.floor(ms / 60000);
    const secs = Math.floor((ms % 60000) / 1000);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  if (isLoading && !isConnected) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mb-4" />
          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Orquestrando Infraestrutura...</p>
        </div>
      </div>
    );
  }

  if (!isConnected) {
    return <ConnectionForm onConnect={handleConnect} isLoading={isLoading} error={error} />;
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <AnimatePresence>
        {sessionExpiry !== null && sessionExpiry < 300000 && sessionExpiry > 0 && (
          <motion.div 
            initial={{ y: -50 }} 
            animate={{ y: 0 }} 
            exit={{ y: -50 }}
            className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-white py-2 px-4 flex items-center justify-center space-x-3 text-[10px] font-black uppercase tracking-widest shadow-lg"
          >
            <SafeIcon icon={FiAlertTriangle} />
            <span>Sessão expira em {formatTime(sessionExpiry)}</span>
          </motion.div>
        )}
      </AnimatePresence>

      <Header 
        currentView={currentView} 
        onViewChange={setCurrentView} 
        onLogout={() => pocketbaseService.disconnect()} 
        connectionInfo={connInfo} 
      />

      <main className="py-6">
        <AnimatePresence mode="wait">
          {currentView === 'dashboard' && (
            <motion.div key="db" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ProjectDashboard />
            </motion.div>
          )}
          {currentView === 'api' && (
            <motion.div key="api" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <ApiHub />
            </motion.div>
          )}
          {currentView === 'config' && (
            <motion.div key="cfg" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <Settings connectionInfo={connInfo} onReconnect={() => setIsConnected(false)} />
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      <footer className="fixed bottom-4 right-6 pointer-events-none">
        <div className="bg-white/80 backdrop-blur-md border border-slate-100 px-4 py-2 rounded-full shadow-sm flex items-center space-x-2">
          <div className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse" />
          <span className="text-[9px] font-black text-slate-400 uppercase">
            Status: Online {sessionExpiry > 0 && `• ${formatTime(sessionExpiry)}`}
          </span>
        </div>
      </footer>
    </div>
  );
}

export default App;